import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { Loader2, Minus, Plus, Trash2, ExternalLink, ShoppingBag } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { useHydrated } from "@/hooks/useHydrated";
import { formatMoney } from "@/lib/shopify";
import { ShopPayBlock } from "@/components/shop-pay-block";
import { ResponsiveImage } from "@/components/responsive-image";
import { SIZES } from "@/lib/image-sources";

export const Route = createFileRoute("/cart")({
  head: () => ({
    meta: [
      { title: "Your Cart — House of Apocrypha" },
      {
        name: "description",
        content: "Review the bottles held in waiting and proceed to a secure checkout.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CartPage,
});

function CartPage() {
  const {
    items,
    isLoading,
    isSyncing,
    updateQuantity,
    removeItem,
    getCheckoutUrl,
    syncCart,
  } = useCartStore();

  useEffect(() => {
    syncCart();
  }, [syncCart]);

  const hydrated = useHydrated();
  const totalItems = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce(
    (s, i) => s + parseFloat(i.price.amount) * i.quantity,
    0,
  );
  const currency = items[0]?.price.currencyCode ?? "USD";

  const handleCheckout = () => {
    const url = getCheckoutUrl();
    if (url) window.location.href = url;
  };

  return (
    <main className="mx-auto min-h-[70vh] max-w-6xl px-6 py-16 sm:py-24">
      <div className="flex items-end justify-between gap-6 border-b border-border pb-6">
        <h1 className="font-display text-4xl tracking-[0.02em] text-parchment sm:text-5xl">
          Your cart
        </h1>
        <Link
          to="/"
          className="font-display text-[15px] tracking-[0.04em] text-gold underline-offset-4 hover:underline"
        >
          Continue shopping
        </Link>
      </div>

      {!hydrated || items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <ShoppingBag className="h-12 w-12 text-foreground/40" />
          <p className="mt-6 font-display text-xl tracking-[0.08em] text-parchment">
            The cart is empty.
          </p>
          <p className="mt-2 text-[15px] text-foreground/75">
            No bottles have been selected yet.
          </p>
          <Link
            to="/products"
            className="mt-8 inline-block border border-gold/70 px-8 py-3 font-display text-[13px] uppercase tracking-[0.18em] text-gold transition-colors hover:border-gold hover:bg-gold/10"
          >
            Browse the collection
          </Link>
        </div>
      ) : (
        <div className="grid gap-12 pt-10 lg:grid-cols-[1.6fr_1fr]">
          {/* Line items */}
          <section>
            <div className="hidden grid-cols-[2fr_1fr_auto] gap-6 pb-4 text-[13px] uppercase tracking-[0.12em] text-foreground/75 sm:grid">
              <span>Product</span>
              <span className="text-center">Quantity</span>
              <span className="text-right">Total</span>
            </div>

            <ul className="divide-y divide-border/60 border-t border-border/60">
              {items.map((item) => {
                const lineTotal =
                  parseFloat(item.price.amount) * item.quantity;
                return (
                  <li
                    key={item.variantId}
                    className="grid grid-cols-1 gap-6 py-8 sm:grid-cols-[2fr_1fr_auto] sm:items-center"
                  >
                    <div className="flex gap-6">
                      <div className="h-32 w-32 flex-shrink-0 overflow-hidden border border-border bg-card">
                        {item.image && (
                          <ResponsiveImage
                            src={item.image.url}
                            alt={item.image.altText || `${item.productTitle}${item.variantTitle && item.variantTitle !== "Default Title" ? `, ${item.variantTitle}` : ""} — bottle photograph`}
                            sizes={SIZES.cartThumb}
                            widths={[96, 192, 288]}
                            pictureClassName="contents"
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex flex-col justify-center">
                        <Link
                          to="/products/$slug"
                          params={{ slug: item.productHandle }}
                          className="font-display text-lg leading-snug tracking-[0.04em] text-parchment hover:text-gold"
                        >
                          {item.productTitle}
                        </Link>
                        {item.variantTitle &&
                          item.variantTitle !== "Default Title" && (
                            <p className="mt-1 text-[15px] text-foreground/75">
                              {item.variantTitle}
                            </p>
                          )}
                        <p className="mt-2 text-[15px] text-gold">
                          {formatMoney(
                            item.price.amount,
                            item.price.currencyCode,
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-start gap-4 sm:justify-center">
                      <div className="inline-flex items-center border border-border">
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(item.variantId, item.quantity - 1)
                          }
                          className="flex h-10 w-10 items-center justify-center text-foreground/75 hover:text-gold"
                          aria-label="Decrease"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="w-12 text-center font-display text-lg text-parchment">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(item.variantId, item.quantity + 1)
                          }
                          disabled={
                            typeof item.maxQuantity === "number" &&
                            item.quantity >= item.maxQuantity
                          }
                          className="flex h-10 w-10 items-center justify-center text-foreground/75 hover:text-gold disabled:cursor-not-allowed disabled:opacity-40"
                          aria-label="Increase"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.variantId)}
                        className="text-foreground/75 hover:text-gold"
                        aria-label="Remove"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>

                    <p className="text-left font-display text-lg text-parchment sm:text-right">
                      {formatMoney(lineTotal, item.price.currencyCode)}
                    </p>
                  </li>
                );
              })}
            </ul>
          </section>

          {/* Summary + checkout */}
          <aside className="space-y-6 border border-border/60 bg-card/30 p-8">
            <div>
              <h2 className="font-display text-[22px] tracking-[0.02em] text-parchment">
                Order summary
              </h2>
              <p className="mt-2 text-[15px] text-foreground/75">
                {totalItems} item{totalItems === 1 ? "" : "s"} held in waiting.
              </p>
            </div>

            <div className="space-y-3 border-t border-border/60 pt-4">
              <div className="flex items-center justify-between text-[16px] text-foreground/75">
                <span>Subtotal</span>
                <span>{formatMoney(subtotal, currency)}</span>
              </div>
              <div className="flex items-baseline justify-between border-t border-border/60 pt-4">
                <span className="font-display text-lg tracking-[0.04em] text-parchment">
                  Estimated total
                </span>
                <span className="font-display text-[30px] leading-none text-parchment">
                  {formatMoney(subtotal, currency)}
                </span>
              </div>
              <p className="text-[14px] text-foreground/70">
                Taxes and delivery confirmed at checkout.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              <button
                type="button"
                onClick={handleCheckout}
                disabled={isLoading || isSyncing}
                className="flex w-full items-center justify-center gap-2 rounded-sm bg-gradient-to-r from-gold-soft to-gold py-4 font-display text-[14px] uppercase tracking-[0.18em] text-ink transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading || isSyncing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <ExternalLink className="h-4 w-4" />
                    Check out
                  </>
                )}
              </button>

              {/* Shop Pay express checkout + installments */}
              {items[0] && (
                <ShopPayBlock
                  variantId={items[0].variantId}
                  quantity={items[0].quantity}
                  price={items[0].price.amount}
                  currency={items[0].price.currencyCode}
                />
              )}

              <p className="pt-2 text-center text-[14px] text-foreground/75">
                More payment options available at checkout.
              </p>
            </div>

            <div className="rounded-sm border border-gold/30 bg-gold/5 p-4">
              <p className="font-display text-[13px] uppercase tracking-[0.12em] text-gold">
                Shipping notice
              </p>
              <p className="mt-1 text-[14px] leading-relaxed text-foreground/80">
                We currently ship only within the United States. European
                shipping is coming soon — if you are outside the U.S. and
                would like to order, please email us at{" "}
                <a
                  href="mailto:hello@houseofapocrypha.com"
                  className="text-gold underline underline-offset-4 hover:text-gold/80"
                >
                  hello@houseofapocrypha.com
                </a>{" "}
                and we will do our best to accommodate you.
              </p>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}
